import { useCallback, useState } from 'react';

import { EdgeTooltipLayer } from '../EdgeTooltipLayer';
import type {
  NetworkGraphEdgeModel,
  NetworkGraphNodeModel,
  NetworkGraphProps,
} from '../NetworkGraph';
import { NetworkGraphEdgeLabel } from '../NetworkGraphEdgeLabel';
import type {
  NetworkGraphPosition,
  NetworkGraphPositions,
} from '../utils/computeRadialLayout';
import { resolveHoverConnectingEdgeId } from '../utils/resolveHoverConnectingEdge';
import type { LabelOffsets } from '../utils/resolveLabelCollisions';
import {
  NetworkGraphEdgeLayer,
  type NetworkGraphRenderableEdge,
} from './NetworkGraphEdgeLayer';
import { NetworkGraphNodeLayer } from './NetworkGraphNodeLayer';

export type { NetworkGraphRenderableEdge } from './NetworkGraphEdgeLayer';

export interface NetworkGraphViewportProps {
  expandedNodeIds: readonly string[];
  externalHoveredNodeId?: string | null;
  labelOffsets: LabelOffsets;
  laneOffsets: Map<string, number>;
  nodeById: Map<string, NetworkGraphNodeModel>;
  nodes: readonly NetworkGraphNodeModel[];
  onEdgeClick?: (edgeId: string) => void;
  onEdgeHover?: (edgeId: string | null) => void;
  onNodeClick?: (nodeId: string) => void;
  onNodeHover?: (nodeId: string | null) => void;
  positions: NetworkGraphPositions;
  rayAngles: Readonly<Record<string, number>>;
  primaryNodeId?: string;
  renderableEdges: readonly NetworkGraphRenderableEdge[];
  renderEdgeTooltip?: NetworkGraphProps['renderEdgeTooltip'];
  suppressAfterDrag: (fn: () => void) => () => void;
  targetAngleOffsets: Map<string, number>;
  visibleEdges: readonly NetworkGraphEdgeModel[];
  connectedPositions: Map<string, NetworkGraphPosition[]>;
}

export const NetworkGraphViewport = ({
  expandedNodeIds,
  externalHoveredNodeId,
  labelOffsets,
  laneOffsets,
  nodeById,
  nodes,
  onEdgeClick,
  onEdgeHover,
  onNodeClick,
  onNodeHover,
  positions,
  rayAngles,
  primaryNodeId,
  renderableEdges,
  renderEdgeTooltip,
  suppressAfterDrag,
  targetAngleOffsets,
  visibleEdges,
  connectedPositions,
}: NetworkGraphViewportProps) => {
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const [pointerNodeId, setPointerNodeId] = useState<string | null>(null);
  // A pointer on the canvas wins over an external hover when both are set.
  const hoveredNodeId = pointerNodeId ?? externalHoveredNodeId ?? null;
  // Pointer chrome lives in each node's local state so NodeLayer props stay
  // stable across canvas hover. Only external hover (and clearing it when a
  // pointer is active) crosses this boundary.
  const nodeLayerHoveredId =
    pointerNodeId !== null ? null : (externalHoveredNodeId ?? null);

  // Stable handlers keep hover updates behind the memoized edge/node layers.
  const handleEdgeHover = useCallback(
    (edgeId: string, hovered: boolean) => {
      setHoveredEdgeId(current =>
        hovered ? edgeId : current === edgeId ? null : current
      );
      onEdgeHover?.(hovered ? edgeId : null);
    },
    [onEdgeHover]
  );
  const handleNodeHover = useCallback(
    (nodeId: string, hovered: boolean) => {
      setPointerNodeId(current =>
        hovered ? nodeId : current === nodeId ? null : current
      );
      onNodeHover?.(hovered ? nodeId : null);
    },
    [onNodeHover]
  );

  const hoverConnectingEdgeId = resolveHoverConnectingEdgeId({
    hoveredNodeId,
    edges: visibleEdges,
    expandedNodeIds,
    primaryNodeId,
  });
  // Both hover sources highlight the connecting edge and reveal its label; the
  // relationship tooltip stays a canvas affordance, so an external hover
  // leaves it closed.
  const elevatedEdgeId = hoveredEdgeId ?? hoverConnectingEdgeId;
  const tooltipEdgeId =
    hoveredEdgeId ?? (pointerNodeId ? hoverConnectingEdgeId : null);
  const elevatedEdge = elevatedEdgeId
    ? renderableEdges.find(({ edge }) => edge.id === elevatedEdgeId)
    : undefined;

  return (
    <>
      <NetworkGraphEdgeLayer
        highlightedEdgeId={hoverConnectingEdgeId}
        laneOffsets={laneOffsets}
        onEdgeClick={onEdgeClick}
        onEdgeHover={handleEdgeHover}
        renderableEdges={renderableEdges}
        suppressAfterDrag={suppressAfterDrag}
        targetAngleOffsets={targetAngleOffsets}
      />
      {renderableEdges.map(renderableEdge =>
        renderableEdge.edge.id === elevatedEdgeId ? null : (
          <NetworkGraphEdgeLabel
            {...renderableEdge}
            isHovered={false}
            key={`${renderableEdge.edge.id}-label`}
            lateralOffset={laneOffsets.get(renderableEdge.edge.id)}
            targetAngleOffset={targetAngleOffsets.get(renderableEdge.edge.id)}
          />
        )
      )}
      <NetworkGraphNodeLayer
        connectedPositions={connectedPositions}
        hoveredNodeId={nodeLayerHoveredId}
        labelOffsets={labelOffsets}
        nodes={nodes}
        onNodeClick={onNodeClick}
        onNodeHover={handleNodeHover}
        positions={positions}
        rayAngles={rayAngles}
        suppressAfterDrag={suppressAfterDrag}
      />
      {elevatedEdge && (
        <NetworkGraphEdgeLabel
          {...elevatedEdge}
          isHovered
          lateralOffset={laneOffsets.get(elevatedEdge.edge.id)}
          targetAngleOffset={targetAngleOffsets.get(elevatedEdge.edge.id)}
        />
      )}
      <EdgeTooltipLayer
        anchorNodeId={hoveredEdgeId ? null : pointerNodeId}
        edgeId={tooltipEdgeId}
        edges={visibleEdges}
        laneOffsets={laneOffsets}
        nodeById={nodeById}
        positions={positions}
        rayAngles={rayAngles}
        renderEdgeTooltip={renderEdgeTooltip}
        targetAngleOffsets={targetAngleOffsets}
      />
    </>
  );
};
